import { createFileRoute } from "@tanstack/react-router";
import { getWebsiteReport } from "@/lib/admin/analytics/reports.functions";
import { BarList, Card, DailyBars, DataTable, Kpi, KpiGrid, Notice, ReportPage, fmt, inr, useReport } from "@/components/admin/analytics/ReportKit";

export const Route = createFileRoute("/admin/analytics")({
  head: () => ({ meta: [{ title: "Website Analytics — CB16 Admin" }, { name: "robots", content: "noindex" }] }),
  component: WebsiteAnalyticsPage,
});

function WebsiteAnalyticsPage() {
  const report = useReport("website", getWebsiteReport);
  return (
    <ReportPage eyebrow="Analytics" title="Website Analytics" description="Real visits to cb16.in from PostHog, alongside completed Live orders." report={report}>
      {(d, r) => {
        const file = `website-${r.range.days[0]}_${r.range.days[r.range.days.length - 1]}`;
        return (
          <>
            {!d.connected ? <Notice tone="error">{d.reason} Visitor figures are not shown rather than estimated.</Notice> : null}
            <KpiGrid>
              <Kpi label="Visits" value={d.connected ? fmt(d.kpis.visits) : "Not connected"} />
              <Kpi label="Unique visitors" value={d.connected ? fmt(d.kpis.uniqueVisitors) : "Not connected"} />
              <Kpi label="Page views" value={d.connected ? fmt(d.kpis.pageviews) : "Not connected"} />
              <Kpi label="Avg session" value={d.connected ? `${d.kpis.avgSessionSec}s` : "Not connected"} />
              <Kpi label="Bounce rate" value={d.connected ? `${d.kpis.bounceRatePct}%` : "Not connected"} sub="Single-page visits" />
              <Kpi label="Orders placed" value={fmt(d.commerce.ordersPlaced)} sub="Completed Live orders" />
              <Kpi label="Revenue" value={inr(d.commerce.revenue)} />
              <Kpi label="Conversion rate" value={d.connected ? `${d.kpis.conversionRatePct}%` : "Not connected"} sub="Orders ÷ visits" />
            </KpiGrid>
            <section className="grid gap-4 lg:grid-cols-2">
              <Card title="Traffic overview (page views per day)" rows={d.connected ? d.traffic : []} file={`${file}-traffic`}>
                {d.connected ? <DailyBars points={d.traffic.map((t) => ({ day: t.day, value: t.pageviews }))} /> : <p className="text-sm text-black/50">Not connected.</p>}
              </Card>
              <Card title="Revenue trend" rows={d.commerce.revenueTrend} file={`${file}-revenue`}>
                <DailyBars points={d.commerce.revenueTrend} format={inr} />
              </Card>
            </section>
            {d.connected ? (
              <>
                <section className="grid gap-4 lg:grid-cols-3">
                  <Card title="Traffic sources" rows={d.sources} file={`${file}-sources`} className="lg:col-span-2">
                    <BarList rows={d.sources.map((s) => ({ label: s.source, value: s.visitors, sharePct: s.sharePct }))} />
                  </Card>
                  <Card title="Devices" rows={d.devices} file={`${file}-devices`}>
                    <BarList rows={d.devices.map((s) => ({ label: s.device, value: s.visitors, sharePct: s.sharePct }))} />
                  </Card>
                </section>
                <Card title="Most visited pages" rows={d.pages} file={`${file}-pages`}>
                  <DataTable head={["Page", "Views", "Visitors"]} rows={d.pages.map((p) => [p.path, fmt(p.views), fmt(p.visitors)])} />
                </Card>
              </>
            ) : null}
            <Card title="Google Search Console">
              <p className="text-sm text-black/50">Not connected. No Search Console account is linked to this project.</p>
            </Card>
            <p className="text-[11px] text-black/40">
              Visits count only cb16.in and www.cb16.in, never preview or other sites. Orders and revenue exclude test, demo, failed, unpaid, cancelled and refunded orders.
            </p>
          </>
        );
      }}
    </ReportPage>
  );
}
